import { useState, useCallback } from 'react'

/**
 * Custom hook for API calls
 * Provides a simple interface for making HTTP requests
 */
export const useApi = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // API calls now use relative URLs since backend is on same domain via Pages Functions
  const API_BASE_URL = '' // Relative URLs: /api/...

  const makeRequest = useCallback(async (url, options = {}) => {
    setLoading(true)
    setError(null)

    // Prepend API base URL if the URL is relative
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`

    try {
      const response = await fetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      })

      if (!response.ok) {
        // For 401 errors on profile endpoints, return null instead of throwing
        if (response.status === 401 && url.includes('/users/profile')) {
          setLoading(false)
          return null
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setLoading(false)
      return data
    } catch (err) {
      setError(err.message)
      setLoading(false)
      throw err
    }
  }, [API_BASE_URL])

  // Specific API methods
  const getUserProfile = useCallback(async () => {
    try {
      return await makeRequest('/api/users/profile')
    } catch (error) {
      // Return null if user is not logged in or API is not available
      console.log('User profile not available:', error.message)
      return null
    }
  }, [makeRequest])

  const getQuestions = useCallback(async (type = 'free') => {
    return await makeRequest(`/api/questions/random?type=${type}`)
  }, [makeRequest])

  const submitTest = useCallback(async (testData) => {
    return await makeRequest('/api/tests/submit', {
      method: 'POST',
      body: JSON.stringify(testData),
    })
  }, [makeRequest])

  const getLeaderboard = useCallback(async (mode = 'global', options = {}) => {
    const { timeFilter = 'all-time' } = options

    // Map frontend filter names to backend API names
    const timeframeMap = {
      'all-time': 'all',
      'monthly': 'month',
      'weekly': 'week'
    }

    const timeframe = timeframeMap[timeFilter] || 'all'

    // Helper to map backend user data to frontend format
    const mapUserData = (backendUser, rank) => ({
      id: `${backendUser.userId || backendUser.id}-${backendUser.testType || 'free'}`, // Make unique per user per test type
      userId: backendUser.userId || backendUser.id,
      name: backendUser.displayName || backendUser.username || 'Anonymous',
      score: backendUser.iqScore || backendUser.score,
      percentile: backendUser.percentile || calculatePercentileFromRank(rank, 100),
      tests: backendUser.totalTests || 1,
      avatar: backendUser.avatar || null,
      testType: backendUser.testType || 'free',
      isCurrentUser: false, // Will be set by auth context
      isFriend: false, // Will be set by friends data
      privacyLevel: 'public'
    })

    const calculatePercentileFromRank = (rank, total) => {
      if (!rank || !total) return 50
      return Math.max(1, Math.min(99, Math.round((1 - (rank / total)) * 100)))
    }

    try {
      if (mode === 'global') {
        const response = await makeRequest(`/api/leaderboard/global?timeframe=${timeframe}&limit=100`)
        const leaderboard = response.data || []

        return {
          users: leaderboard.map((user, index) => mapUserData(user, user.rank || index + 1)),
          userRank: null
        }
      } else if (mode === 'friends') {
        // Friends leaderboard requires userId and friendIds
        // For now, return empty array - will be implemented when auth is ready
        return {
          users: [],
          userRank: null
        }
      } else if (mode === 'category') {
        // Category leaderboard - can reuse global with different filters
        const response = await makeRequest(`/api/leaderboard/global?timeframe=${timeframe}&limit=100`)
        const leaderboard = response.data || []

        return {
          users: leaderboard.map((user, index) => mapUserData(user, user.rank || index + 1)),
          userRank: null
        }
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error)
      throw error
    }
  }, [makeRequest])

  const apiCall = useCallback(async (endpoint, options = {}) => {
    // Generic API call method
    const url = endpoint.startsWith('http') ? endpoint : `/api${endpoint}`
    return await makeRequest(url, options)
  }, [makeRequest])

  const linkTestResults = useCallback(async (userId) => {
    // Link anonymous test results to user account after login
    const sessionId = localStorage.getItem('iqtest_session_id')

    if (!sessionId) {
      console.log('No session ID found to link')
      return { linkedResults: 0 }
    }

    try {
      const response = await makeRequest('/api/tests/link-results', {
        method: 'POST',
        body: JSON.stringify({ userId, sessionId })
      })

      console.log('Test results linked:', response.data)
      return response.data
    } catch (error) {
      console.error('Failed to link test results:', error)
      return { linkedResults: 0 }
    }
  }, [makeRequest])

  return {
    loading,
    error,
    makeRequest,
    getUserProfile,
    getQuestions,
    submitTest,
    getLeaderboard,
    apiCall,
    linkTestResults,
  }
}

export default useApi